// -- Tooltip --------------------------------------------------
// Popover API tooltips with delay, group behavior, ARIA wiring,
// CSS anchor positioning, and scroll dismiss, plus the named-state API
// so agents/tests can drive visibility by name (AGENTS.md "State API").

// Shared preamble (AGENTS.md "State API"); the implementation lives in core.js —
// build.ts rewrites this import into a df$.shadcn.shared binding in dist/.
import { defussGlobals, safeShowPopover, defussQuery, componentState, bindComponent } from '../../../shared/state-api.js';

const df$ = defussGlobals();
const dfDollar = defussQuery();

const tooltipStates = ['default', 'visible'];

// VERIFIED: (verify's API docs gate) the states below are exactly the declared ones, each
// described, and every config field typed, described and named in the code.
/** setState() configs per state - the tooltip's states take none. */
export interface TooltipStateConfigs {
  /** Hidden. */
  default: {};
  /** Shown (popover="hint"), anchored to its trigger. */
  visible: {};
}

/**
 * The markup of a state: none - 'visible' lives in the top layer
 * (:popover-open), not in an attribute, so every state renders the authored
 * markup. render() stays the State API's markup function all the same.
 */
function applyMarkup(_el, _stateName) {}

/**
 * UI side of setState: 'default' hides, 'visible' shows immediately
 * (bypasses the hover delay - a declared state is imperative, not hover-sim).
 */
function triggerStateChange(tip, stateName, _config) {
  switch (stateName) {
    case 'default':
      try { tip.hidePopover(); } catch { /* already closed */ }
      break;
    case 'visible':
      // deferred show (safeShowPopover): showPopover() mid-exit (after
      // scroll-hiding) crashes the headless renderer; hints stay hints.
      safeShowPopover(tip);
      markGroupOpen();
      break;
  }
}

/** Registry-level API; pass the tooltip element explicitly. Unknown names throw. */
export const tooltipApi = componentState({
  component: 'tooltip',
  states: tooltipStates,
  apply: (tip, state) => triggerStateChange(tip, state.name, state.config),
  markup: (el, state) => applyMarkup(el, state.name),
});

df$.tooltipApi = tooltipApi;
df$.tooltipStates = tooltipStates;

const DELAY_DEFAULT = 700;      // ms before first tooltip opens
const CLOSE_DELAY_DEFAULT = 0;  // ms before tooltip closes
const GROUP_TIMEOUT = 400;      // ms after last tooltip hides before delay resets

let groupOpen = false;       // true while any tooltip is visible
let groupTimer = null;       // timeout to reset groupOpen

function markGroupOpen() {
  groupOpen = true;
  clearTimeout(groupTimer);
}

function scheduleGroupReset() {
  clearTimeout(groupTimer);
  groupTimer = setTimeout(() => { groupOpen = false; }, GROUP_TIMEOUT);
}

function init() {
dfDollar('[data-tooltip-trigger]:not([data-init])').toArray().forEach((trigger) => {
  trigger.dataset.init = '';
  const tip = dfDollar('#' + CSS.escape(trigger.dataset.tooltipTrigger)).get(0);
  if (!tip) return;

  // CSS anchor positioning - unique name per trigger-tooltip pair
  const anchorId = `--tooltip-${tip.id}`;
  trigger.style.anchorName = anchorId;
  tip.style.positionAnchor = anchorId;

  // ARIA - link trigger to tooltip
  trigger.setAttribute('aria-describedby', tip.id);

  const delay = Number(trigger.dataset.delay ?? DELAY_DEFAULT);
  const closeDelay = Number(trigger.dataset.closeDelay ?? CLOSE_DELAY_DEFAULT);

  let openTimer = null;
  let closeTimer = null;

  function show() {
    clearTimeout(closeTimer);
    clearTimeout(openTimer);
    const wait = groupOpen ? 0 : delay;
    openTimer = setTimeout(() => {
      try { tip.showPopover(); } catch { /* already open */ }
      markGroupOpen();
    }, wait);
  }

  function hide() {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    closeTimer = setTimeout(() => {
      try { tip.hidePopover(); } catch { /* already closed */ }
      scheduleGroupReset();
    }, closeDelay);
  }

  trigger.addEventListener('mouseenter', show);
  trigger.addEventListener('mouseleave', hide);
  trigger.addEventListener('focus', show);
  trigger.addEventListener('blur', hide);
});

  // bind-scope the api per tooltip instance: `$('#tip').api.setState('visible')`
  dfDollar('.tooltip[popover]:not([data-init])').toArray().forEach((tip) => {
    tip.dataset.init = '';
    // el.store + el.api (AGENTS.md "State through stores")
    bindComponent(tip, tooltipApi);
  });
}

init();
new MutationObserver(init).observe(document, { childList: true, subtree: true });

// -- Scroll dismiss -------------------------------------------
// Hide any open tooltip when the page scrolls.
if (!document.__tooltipScrollInit) {
  document.__tooltipScrollInit = true;
  document.addEventListener('scroll', () => {
    dfDollar('.tooltip:popover-open').toArray().forEach((tip) => {
      try { tip.hidePopover(); } catch {}
    });
  }, { passive: true, capture: true });
}
